// src/pages/guides/TLSSSLDriftGuide.js
// TLS/SSL Drift Guide (G3) - SEO Guides Program

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function TLSSSLDriftGuide() {
  const startTrial = useTrialCta('guide_trial:tls-ssl-drift');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "TLS/SSL Drift: When Certificates Change Without Warning",
    "description": "Learn why SSL certificate monitoring goes beyond expiry dates. Detect cipher downgrades, CA changes, and unexpected renewals that signal compromise.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-03-06T00:00:00-05:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/tls-ssl-drift",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/tls-ssl-drift",
    "keywords": ["SSL certificate monitoring", "TLS drift detection", "certificate change monitoring", "cipher suite downgrade", "TLS security"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "TLS/SSL Drift" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="TLS/SSL Drift: Certificate Change Monitoring | DriftAlarm"
        description="Learn why SSL certificate monitoring goes beyond expiry dates. Detect cipher downgrades, CA changes, and unexpected renewals that signal compromise."
        path="/guides/tls-ssl-drift"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/tls-ssl-drift')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>TLS/SSL Drift: When Certificates Change Without Warning</h1>
          <GuideByline path="/guides/tls-ssl-drift" />
          <GuideAnswer path="/guides/tls-ssl-drift" />
          <p className="guide-hero-intro">
            Most SSL certificate monitoring stops at expiry dates. But the changes that actually signal
            compromise&mdash;a swapped certificate authority, a cipher suite downgrade, a renewal nobody
            requested&mdash;happen silently. TLS drift detection catches the changes that expiry alerts miss.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Why certificate expiry is only the surface of TLS monitoring</li>
              <li>Four types of TLS/SSL drift and what each one means</li>
              <li>When TLS drift signals compromise vs. routine operations</li>
              <li>Manual checking vs. continuous monitoring (with comparison)</li>
              <li>A practical TLS monitoring checklist you can implement today</li>
            </ul>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span className="guide-card-read-time" style={{ fontSize: '0.85rem' }}>14 min read</span>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/configuration-drift" className="da-btn da-btn--secondary">
              Back to Configuration Drift Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#beyond-expiry">Beyond Certificate Expiry</a></li>
            <li><a href="#four-types">4 Types of TLS/SSL Drift</a></li>
            <li><a href="#compromise-vs-operations">Compromise vs. Operations</a></li>
            <li><a href="#impact">Impact and Exposure</a></li>
            <li><a href="#manual-vs-continuous">Manual vs. Continuous Monitoring</a></li>
            <li><a href="#checklist">TLS Monitoring Checklist</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section 1: Beyond Certificate Expiry */}
        <section id="beyond-expiry" className="guide-section">
          <h2>Certificate Expiry Is Just the Beginning</h2>

          <p>
            When most teams think about SSL certificate monitoring, they think about one thing: expiry dates.
            And that makes sense&mdash;an expired certificate breaks your site, triggers browser warnings, and
            erodes customer trust. But expiry monitoring only watches one attribute of a certificate. The TLS
            configuration protecting your traffic includes dozens of parameters, and any one of them can change
            in ways that weaken your security posture.
          </p>

          <p>
            TLS drift is any unexpected change to your TLS/SSL configuration&mdash;the certificate itself,
            the certificate authority that issued it, the cipher suites negotiated, or the subject names it
            covers. These changes happen for many reasons: automated renewal tools, infrastructure migrations,
            CDN reconfigurations, or deliberate attacker action. The problem is that without continuous
            monitoring, legitimate changes and malicious changes look identical: silent.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">SSL Certificate Monitoring Beyond Dates</div>
            <div className="callout-content">
              <p>
                <strong>Expiry monitoring answers one question: "Is the certificate still valid?"</strong> TLS
                drift detection answers a harder one: "Has anything about this certificate or its configuration
                changed since I last checked&mdash;and should it have?"
              </p>
            </div>
          </div>

          <p>
            Consider what a certificate change can mean. If your certificate was issued by DigiCert yesterday
            and today it is issued by Let's Encrypt, that might be an ops team switching providers&mdash;or it
            might be an attacker who obtained a fraudulent certificate. If your server negotiated TLS 1.3 last
            week and TLS 1.1 today, someone either misconfigured something or downgraded your encryption
            deliberately. Without a baseline and continuous comparison, you cannot tell the difference.
          </p>
        </section>

        {/* Section 2: Four Types of TLS/SSL Drift */}
        <section id="four-types" className="guide-section">
          <h2>4 Types of TLS/SSL Drift</h2>

          <p>
            Not all certificate changes carry the same risk. Understanding the four categories of TLS drift
            helps you prioritize response and build meaningful alerting rules.
          </p>

          <h3>1. Certificate Authority (CA) Change</h3>
          <p>
            The issuing CA changes between observations. Your certificate was signed by Sectigo; now it is
            signed by a different authority entirely. CA changes happen during planned migrations&mdash;moving
            from a paid CA to Let's Encrypt, for example&mdash;but they also happen when an attacker obtains
            a certificate from a CA that does not require strong domain validation.
          </p>
          <ul>
            <li><strong>Legitimate cause:</strong> CA vendor switch, cost optimization, automation tooling change</li>
            <li><strong>Suspicious cause:</strong> Unauthorized certificate issuance, compromised domain validation</li>
            <li><strong>Detection signal:</strong> Issuer Organization or Issuer CN field differs from baseline</li>
          </ul>

          <h3>2. Cipher Suite Downgrade</h3>
          <p>
            The negotiated cipher suites weaken between observations. Your server previously required TLS 1.3
            with AEAD ciphers; now it accepts TLS 1.0 with CBC-mode ciphers or even export-grade suites. Cipher
            downgrades can result from server reconfigurations, load balancer changes, or active
            man-in-the-middle (MITM) attacks stripping encryption strength.
          </p>
          <ul>
            <li><strong>Legitimate cause:</strong> Backward compatibility requirement, misconfigured deployment</li>
            <li><strong>Suspicious cause:</strong> MITM interception, deliberate weakening for traffic inspection</li>
            <li><strong>Detection signal:</strong> Supported protocol versions or cipher list includes deprecated algorithms</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Cipher Downgrades Are High Priority</div>
            <div className="callout-content">
              <p>
                <strong>A cipher suite downgrade directly reduces the cryptographic strength protecting your
                traffic.</strong> Even if the cause is operational (a new load balancer deployed with default
                settings), the window of exposure is real. Treat cipher downgrades as P1 until you confirm the
                root cause and remediate.
              </p>
            </div>
          </div>

          <h3>3. SAN/CN Mismatch</h3>
          <p>
            The Subject Alternative Names (SANs) or Common Name (CN) on the certificate change unexpectedly.
            A certificate that covered <code>www.example.com</code> and <code>api.example.com</code> now
            covers <code>www.example.com</code> and <code>staging.example.com</code>&mdash;or includes
            domains that do not belong to your organization at all. SAN drift can indicate certificate
            reissuance errors, shared hosting changes, or domain hijacking.
          </p>
          <ul>
            <li><strong>Legitimate cause:</strong> Certificate renewal with updated subdomain list</li>
            <li><strong>Suspicious cause:</strong> Shared certificate from compromised host, domain takeover</li>
            <li><strong>Detection signal:</strong> SAN list differs from baseline; unexpected domains present</li>
          </ul>

          <h3>4. Unexpected Renewal or Reissuance</h3>
          <p>
            The certificate serial number or fingerprint changes outside of your known renewal window. If
            your certificate was not due for renewal for another six months but a new one appeared today,
            something triggered that reissuance. It could be an ACME bot, a team member who rotated
            credentials, or an attacker who gained control of your domain validation process.
          </p>
          <ul>
            <li><strong>Legitimate cause:</strong> Automated renewal, key rotation policy. Note that maximum public certificate lifetimes are shrinking on the CA/Browser Forum schedule (200 days for certificates issued from March 2026, dropping further in 2027 and 2029), so reissuance is now routine — judge an unexpected renewal against your automation's schedule, not against how often it happens</li>
            <li><strong>Suspicious cause:</strong> Unauthorized certificate request via compromised DNS or web validation</li>
            <li><strong>Detection signal:</strong> Certificate serial number or SHA-256 fingerprint changes before expected renewal date</li>
          </ul>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Drift Type</th>
                  <th>Risk Level</th>
                  <th>Detection Field</th>
                  <th>Response Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CA Change</td>
                  <td>High</td>
                  <td>Issuer DN</td>
                  <td>24 hours</td>
                </tr>
                <tr>
                  <td>Cipher Downgrade</td>
                  <td>Critical</td>
                  <td>Protocol version, cipher list</td>
                  <td>4 hours</td>
                </tr>
                <tr>
                  <td>SAN/CN Mismatch</td>
                  <td>Medium-High</td>
                  <td>SAN list, CN field</td>
                  <td>24 hours</td>
                </tr>
                <tr>
                  <td>Unexpected Renewal</td>
                  <td>Medium-High</td>
                  <td>Serial number, fingerprint</td>
                  <td>24 hours</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 3: Compromise vs. Operational Change */}
        <section id="compromise-vs-operations" className="guide-section">
          <h2>When TLS Drift Signals Compromise vs. Operational Change</h2>

          <p>
            The challenge with TLS drift detection is not finding changes&mdash;it is determining whether a
            change is expected. Most certificate changes are routine. Automated renewal tools like Certbot
            and cloud-managed certificates replace certificates regularly. The goal is to separate the signal
            from the noise.
          </p>

          <h3>Indicators of Legitimate Operational Change</h3>
          <ul>
            <li>Change coincides with a scheduled maintenance window or deployment</li>
            <li>New certificate issued by the same CA as the previous one</li>
            <li>SAN list matches or is a superset of the previous certificate</li>
            <li>Cipher suites remain at the same strength or improve</li>
            <li>Change is documented in your ITSM or deployment pipeline</li>
          </ul>

          <h3>Indicators That Warrant Investigation</h3>

          <div className="callout callout-warning">
            <div className="callout-title">Investigate These TLS Changes Immediately</div>
            <div className="callout-content">
              <ul>
                <li>CA changed to a provider your organization does not use</li>
                <li>Certificate issued by a free CA when your policy requires EV or OV certificates</li>
                <li>Cipher suites downgraded to include known-weak algorithms (RC4, DES, export ciphers)</li>
                <li>TLS protocol version dropped below 1.2</li>
                <li>SAN list includes domains outside your organization</li>
                <li>Renewal occurred but no team member or automation initiated it</li>
                <li>Certificate transparency logs show issuance you did not request</li>
              </ul>
            </div>
          </div>

          <div className="callout callout-critical">
            <div className="callout-title">High-Confidence Compromise Indicators</div>
            <div className="callout-content">
              <p>
                <strong>Treat these as P0 until proven otherwise:</strong> Certificate issued by an unknown or
                internal-only CA appearing on a public-facing service. Self-signed certificate replacing a
                CA-signed one. Certificate with a wildcard scope broader than your domain. Any TLS change
                combined with DNS record changes you did not authorize.
              </p>
            </div>
          </div>

          <p>
            The key principle is correlation. A single TLS change is ambiguous. A TLS change combined with a
            DNS record modification, a new IP address, or a change in HTTP response headers is a pattern that
            demands investigation. This is why TLS drift monitoring is most effective when combined with broader
            configuration drift detection across your entire attack surface.
          </p>
        </section>

        {/* Section 4: Impact */}
        <section id="impact" className="guide-section">
          <h2>Impact: What Undetected TLS Drift Costs You</h2>

          <p>
            TLS drift that goes undetected creates concrete security and business consequences. The impact
            depends on the type of drift and how long it persists.
          </p>

          <h3>Man-in-the-Middle Exposure</h3>
          <p>
            A cipher suite downgrade or fraudulent certificate creates a window where traffic between your
            users and your servers can be intercepted. Attackers who control a network path&mdash;via BGP
            hijacking, DNS spoofing, or compromised infrastructure&mdash;can decrypt, read, and modify
            data in transit. The exposure window lasts until the drift is detected and remediated.
          </p>

          <h3>Compliance Failures</h3>
          <p>
            Regulatory frameworks mandate specific TLS configurations. PCI DSS requires TLS 1.2 or higher.
            HIPAA demands encryption in transit for protected health information. NIST SP 800-52 specifies
            approved cipher suites. A cipher downgrade that introduces TLS 1.0 support puts you out of
            compliance instantly, even if no data is actually intercepted.
          </p>
          <ul>
            <li><strong>PCI DSS:</strong> TLS 1.2+ required; TLS 1.0/1.1 explicitly prohibited since June 2018</li>
            <li><strong>HIPAA:</strong> Encryption in transit required for ePHI; weak ciphers constitute a technical safeguard failure</li>
            <li><strong>SOC 2:</strong> Encryption controls are evaluated; drift from documented configuration is a finding</li>
          </ul>

          <h3>Browser Trust Warnings</h3>
          <p>
            SAN/CN mismatches cause browsers to display security warnings that erode user trust and kill
            conversion rates. A certificate that no longer covers the hostname users are visiting triggers
            an interstitial warning page. Users see "Your connection is not private" and leave. For
            e-commerce, SaaS, and financial services, even minutes of certificate-related warnings translate
            to measurable revenue loss.
          </p>

          <div className="example-box">
            <div className="example-title">Example Scenario: TLS Drift (★ illustrative, not a customer case)</div>
            <div className="example-content">
              <p>
                A SaaS company migrates their CDN provider. The new CDN deploys with a shared certificate
                that covers 40 customer domains on a single IP. The cipher configuration defaults to the
                CDN's standard profile, which includes TLS 1.0 for backward compatibility. For three weeks,
                the company's API traffic is protected by weaker encryption than their security policy requires.
                A compliance audit catches it. The remediation takes an hour; the audit finding takes months
                to close.
              </p>
              <p>
                <strong>Continuous TLS monitoring would have caught this on day one.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Manual vs. Continuous Monitoring */}
        <section id="manual-vs-continuous" className="guide-section">
          <h2>Manual Checking vs. Continuous TLS Monitoring</h2>

          <p>
            Many teams rely on manual certificate checks using <code>openssl s_client</code> or browser
            inspection. This works for spot checks, but it does not scale and it does not catch drift
            between checks.
          </p>

          <h3>Manual Approach: openssl s_client</h3>
          <div className="scoring-formula">
            <div className="scoring-formula-title">Manual TLS Check</div>
            <div className="scoring-formula-line"><code>openssl s_client -connect example.com:443 -servername example.com</code></div>
            <div className="scoring-formula-line"># Shows certificate chain, expiry, cipher</div>
            <div className="scoring-formula-line"># Must be run per-host, per-port, manually</div>
            <div className="scoring-formula-line"># No historical comparison or alerting</div>
          </div>

          <p>
            Manual checking gives you a point-in-time snapshot. It tells you what the TLS configuration looks
            like right now, but not whether it changed since your last check. For organizations with dozens or
            hundreds of TLS endpoints, manual checks are impractical as a primary monitoring strategy.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Manual (openssl)</th>
                  <th>Continuous Monitoring</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Expiry alerting</td>
                  <td>No (requires scripting)</td>
                  <td>Yes, automated</td>
                </tr>
                <tr>
                  <td>CA change detection</td>
                  <td>Only if you compare manually</td>
                  <td>Automatic baseline comparison</td>
                </tr>
                <tr>
                  <td>Cipher suite tracking</td>
                  <td>Point-in-time only</td>
                  <td>Historical trend with drift alerts</td>
                </tr>
                <tr>
                  <td>SAN/CN monitoring</td>
                  <td>Manual comparison required</td>
                  <td>Automatic diff on every scan</td>
                </tr>
                <tr>
                  <td>Scale (100+ endpoints)</td>
                  <td>Impractical without automation</td>
                  <td>Built for scale</td>
                </tr>
                <tr>
                  <td>Notification speed</td>
                  <td>Days to weeks (next manual check)</td>
                  <td>Minutes to hours</td>
                </tr>
                <tr>
                  <td>Compliance evidence</td>
                  <td>Requires manual documentation</td>
                  <td>Audit trail generated automatically</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            The gap between manual and continuous monitoring is not just speed&mdash;it is coverage. Manual
            checks happen when someone remembers to run them. Continuous monitoring runs on a schedule,
            compares against a known baseline, and alerts on deviation. For TLS drift, where the window
            between a change and exploitation can be hours, that difference matters.
          </p>

          {/* Mid-article CTA */}
          <div className="callout callout-info">
            <div className="callout-title">How DriftAlarm Tracks TLS Changes</div>
            <div className="callout-content">
              <p>
                DriftAlarm's discovery and vulnerability scans capture TLS configuration on every endpoint
                automatically. When a certificate authority changes, a cipher suite weakens, or a SAN list
                shifts, the drift detection engine compares against your baseline and fires an alert. No
                scripts to maintain, no manual comparison required.
              </p>
              <p>
                <button
                  className="da-btn da-btn--primary"
                  onClick={startTrial}
                  style={{ marginTop: '10px', fontSize: '0.85rem', padding: '12px 24px' }}
                >
                  <span>Start Free 30-Day Trial</span>
                  <span>&rarr;</span>
                </button>
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: TLS Monitoring Checklist */}
        <section id="checklist" className="guide-section">
          <h2>What to Do Now: TLS Monitoring Checklist</h2>

          <p>
            Whether you build your own monitoring or use a platform, these five steps establish a functional
            TLS drift detection capability.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Inventory All TLS Endpoints</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Baseline Current Certificates</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Define Drift Alert Rules</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Automate Continuous Scanning</div>
            </div>
            <div className="step-card">
              <div className="step-card-number">5</div>
              <div className="step-card-title">Review and Respond to Drift</div>
            </div>
          </div>

          <h3>Step 1: Inventory All TLS Endpoints</h3>
          <p>
            List every domain, subdomain, IP address, and port where TLS is active. Include your primary
            web properties, API endpoints, mail servers (SMTP STARTTLS), and any internal services exposed
            externally. You cannot monitor what you have not inventoried.
          </p>

          <h3>Step 2: Baseline Current Certificates</h3>
          <p>
            For each endpoint, record the current certificate fingerprint, issuing CA, expiry date, SAN list,
            and negotiated cipher suite. This baseline becomes your reference point. Any future change is
            measured against it. Store baselines in a system that timestamps them automatically.
          </p>

          <h3>Step 3: Define Drift Alert Rules</h3>
          <p>
            Not every change requires the same response. Define rules that match your risk tolerance:
          </p>
          <ul>
            <li><strong>Critical alert:</strong> Cipher downgrade to TLS 1.0/1.1, self-signed certificate on public endpoint</li>
            <li><strong>High alert:</strong> CA change, unexpected renewal, SAN list includes unknown domains</li>
            <li><strong>Medium alert:</strong> Certificate renewed by expected automation, SAN list expanded with known subdomains</li>
            <li><strong>Informational:</strong> Certificate expiry approaching (30/14/7 day thresholds)</li>
          </ul>

          <h3>Step 4: Automate Continuous Scanning</h3>
          <p>
            Run TLS checks on a schedule&mdash;daily at minimum for production endpoints, weekly for lower-risk
            services. Automation eliminates the human element that makes manual monitoring unreliable. Whether
            you script <code>openssl</code> checks in cron or use a dedicated monitoring platform, the key
            is consistency.
          </p>

          <h3>Step 5: Review and Respond to Drift</h3>
          <p>
            When drift is detected, follow a consistent process: verify the change, correlate with change
            management records, determine if the change was authorized, and remediate if it was not. Document
            your findings for compliance audit trails. Over time, your drift rules will become more precise as
            you learn which changes are routine in your environment.
          </p>

          <div className="callout callout-success">
            <div className="callout-title">Measure Your Coverage</div>
            <div className="callout-content">
              <p>
                Track two metrics to assess your TLS monitoring maturity: <strong>endpoint coverage</strong>
                (percentage of TLS endpoints under continuous monitoring) and <strong>mean time to detect
                drift</strong> (how long between a TLS change and your first alert). Aim for 100% coverage
                and sub-24-hour detection for production assets.
              </p>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            TLS/SSL drift is one component of broader configuration drift monitoring. Explore these
            related guides for a complete picture of attack surface change detection:
          </p>
          <ul>
            <li>
              <Link to="/guides/configuration-drift">Configuration Drift Guide</Link> — The parent
              pillar covering all forms of infrastructure drift, including network, cloud, and application
              configuration changes.
            </li>
            <li>
              <Link to="/guides/dns-drift-detection">DNS Drift Detection Guide</Link> — DNS changes
              often accompany TLS drift. A record pointing to a new IP may also mean a new certificate.
              Monitor both together.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — The
              comprehensive guide to External Attack Surface Management, covering discovery, analysis, and
              continuous monitoring.
            </li>
            <li>
              <Link to="/ssl-certificate-monitoring">SSL Certificate Monitoring</Link> — See how DriftAlarm
              provides continuous SSL certificate monitoring with automated drift detection across your
              entire attack surface.
            </li>
          </ul>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section">
          <h2>Start Detecting TLS Drift Today</h2>
          <p>
            Certificate expiry alerts are table stakes. Real TLS security monitoring means detecting
            CA changes, cipher downgrades, and unexpected renewals before they become incidents. DriftAlarm
            baselines your TLS configuration and alerts you the moment something shifts.
          </p>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <Link to="/upgrade" className="da-btn da-btn--primary">
              <span>View Pricing</span>
              <span>&rarr;</span>
            </Link>
            <button className="da-btn da-btn--secondary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/tls-ssl-drift" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default TLSSSLDriftGuide;
